import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Accordion,
  AccordionItem,
  Button,
  DescriptionList,
  InputField,
  Modal,
  MultiselectCombobox,
  Select,
  Stepper,
} from '@enamelai/design-system'
import {
  ProviderWorkPosition,
  WorkStatus,
  isProviderSeatRole,
} from '@constants/Providers'
import { formatName } from '@utils/name-formatter.util'
import {
  createEmptyProviderData,
  getChangedProviderFields,
  mapDatabaseToProviderData,
} from '@utils/provider.mapper'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { addProvider, updateProvider } from '@store/actions'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import type { ProviderData } from '@dataTypes/provider.types'
import {
  getAllStaffs,
  getProviderSeatLimit,
  sendStaffEmail,
} from '@services/api/provider.api'
import './AddStaffStyle.css'
import { MOCK_CLINICS } from '@assets/mockdata/Clinic/mockClinics.data'
import { MOCK_PROVIDERS } from '@assets/mockdata/Provider/mockProviders.data'
import { Provider } from '@dataTypes/provider.types'
import { getEnv } from '@utils/env'

export type StaffSettingsFormData = {
  firstName: string
  lastName: string
  email: string
  username: string
  clinicIds: string
  workRole: string
}

type AddStaffProps = {
  isOpen: boolean
  onRequestClose: () => void
  onSubmit?: (data: StaffSettingsFormData) => void
  /** Called after staff is created or updated successfully (default dispatch path). */
  onSaved?: () => void
  /** Opens the modal in edit mode, prefilled from this staff member. */
  staffToEdit?: Provider | null
}

const emptyForm = (): StaffSettingsFormData => ({
  firstName: '',
  lastName: '',
  email: '',
  username: '',
  clinicIds: '',
  workRole: '',
})

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const STEPS = ['Personal info', 'Review']
const SEAT_LIMIT_NOTE =
  'Note: Provider limit reached. Kindly contact your superadmin.'

const splitIds = (value: string): string[] =>
  value
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean)

const AddStaff: React.FC<AddStaffProps> = ({
  isOpen,
  onRequestClose,
  onSubmit,
  onSaved,
  staffToEdit = null,
}) => {
  const initialProviderData = useMemo(
    () => (staffToEdit ? mapDatabaseToProviderData(staffToEdit) : null),
    [staffToEdit],
  )
  const isEdit = initialProviderData != null
  const originalWorkRole = initialProviderData?.workRole ?? ''
  const dispatch = useAppDispatch()
  const { showToast } = useNotificationToast()
  const clinics = useAppSelector((state) => state.clinic.data)
  const selectedClinicId = useAppSelector(
    (state) => state.clinic.selectedClinic?.clinic_id,
  )
  const resolvedClinics = useMemo(() => {
    if (clinics.length > 0) return clinics
    if (getEnv('REACT_APP_USE_SETTINGS_MOCKS', '') === 'true') {
      return MOCK_CLINICS
    }
    return []
  }, [clinics])

  const clinicOptions = useMemo(
    () =>
      resolvedClinics
        .map((c) => ({ value: c.clinic_id, label: c.clinic_name }))
        .filter(
          (clinic, index, self) =>
            self.findIndex((c) => c.value === clinic.value) === index,
        ),
    [resolvedClinics],
  )

  const [step, setStep] = useState(1)
  const [formData, setFormData] = useState<StaffSettingsFormData>(emptyForm())
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string
  }>({})
  const [seatMetaLoading, setSeatMetaLoading] = useState(false)
  const [providerSeatLimitState, setProviderSeatLimitState] = useState<
    number | null
  >(null)
  const [activeProviderCountForLimit, setActiveProviderCountForLimit] =
    useState(0)

  const wasModalOpenRef = useRef(false)

  useEffect(() => {
    const justOpened = isOpen && !wasModalOpenRef.current
    wasModalOpenRef.current = isOpen
    if (!justOpened) {
      return
    }
    setStep(1)
    setValidationErrors({})
    if (initialProviderData) {
      const assignedIds = initialProviderData.clinicIds?.length
        ? initialProviderData.clinicIds
        : [initialProviderData.clinicId].filter(Boolean)
      setFormData({
        firstName: initialProviderData.firstName ?? '',
        lastName: initialProviderData.lastName ?? '',
        email: initialProviderData.email ?? '',
        username: initialProviderData.username ?? '',
        clinicIds: assignedIds.join(','),
        workRole: initialProviderData.workRole ?? '',
      })
      return
    }
    const defaultIds: string[] = []
    if (selectedClinicId) {
      defaultIds.push(selectedClinicId)
    } else if (resolvedClinics[0]?.clinic_id) {
      defaultIds.push(resolvedClinics[0].clinic_id)
    }
    setFormData({
      ...emptyForm(),
      clinicIds: defaultIds.join(','),
    })
  }, [isOpen, initialProviderData, resolvedClinics, selectedClinicId])

  useEffect(() => {
    if (!isOpen) {
      return
    }
    let cancelled = false
    setSeatMetaLoading(true)
    void Promise.all([getAllStaffs(), getProviderSeatLimit()])
      .then(([staffRes, limitRes]) => {
        if (cancelled) return
        let list: Provider[] = staffRes.data?.staffs ?? []
        const useSettingsMocks =
          getEnv('REACT_APP_USE_SETTINGS_MOCKS', '') === 'true'
        if (list.length === 0 && useSettingsMocks) {
          const mockClinicIds = MOCK_CLINICS.map((c) => c.clinic_id)
          const fallbackClinicId = mockClinicIds[0] ?? 'mock-clinic-1'
          list = MOCK_PROVIDERS.map((p, idx) => ({
            ...p,
            clinic_id:
              mockClinicIds[idx % mockClinicIds.length] ?? fallbackClinicId,
          }))
        }
        let apCount = 0
        for (const s of list) {
          if ((s.work_status || '') !== WorkStatus.ACTIVE) continue
          if (isProviderSeatRole(s.work_role)) apCount += 1
        }
        setActiveProviderCountForLimit(apCount)
        if (
          limitRes.statusCode === 200 &&
          limitRes.data &&
          typeof limitRes.data.provider_limit === 'number'
        ) {
          setProviderSeatLimitState(limitRes.data.provider_limit)
        } else {
          setProviderSeatLimitState(null)
        }
        setSeatMetaLoading(false)
      })
      .catch(() => {
        if (cancelled) return
        setProviderSeatLimitState(null)
        setSeatMetaLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [isOpen])

  const atProviderSeatLimit = useMemo(
    () =>
      !seatMetaLoading &&
      providerSeatLimitState != null &&
      activeProviderCountForLimit >= providerSeatLimitState,
    [seatMetaLoading, providerSeatLimitState, activeProviderCountForLimit],
  )

  const workRoleOptions = useMemo(
    () =>
      Object.values(ProviderWorkPosition)
        .filter(
          (role) =>
            role === originalWorkRole ||
            !(atProviderSeatLimit && isProviderSeatRole(role)),
        )
        .map((role) => ({ value: role, label: role })),
    [atProviderSeatLimit, originalWorkRole],
  )

  // Editing someone who already holds a provider seat doesn't consume a new one.
  const keepsExistingSeat =
    isEdit &&
    isProviderSeatRole(originalWorkRole) &&
    isProviderSeatRole(formData.workRole)

  const handleInputChange = useCallback(
    (field: keyof StaffSettingsFormData, value: string): void => {
      setFormData((prev) => ({ ...prev, [field]: value }))
      setValidationErrors((prev) => {
        if (prev[field]) {
          const next = { ...prev }
          delete next[field]
          return next
        }
        return prev
      })
    },
    [],
  )

  const handleNameBlur = useCallback(
    (field: 'firstName' | 'lastName', value: string): void => {
      const formatted = formatName(value)
      if (formatted !== value) {
        setFormData((prev) => ({ ...prev, [field]: formatted }))
      }
    },
    [],
  )

  const validateStep1 = useCallback((): boolean => {
    const errors: { [key: string]: string } = {}
    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required'
    }
    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required'
    }
    if (!formData.email.trim()) {
      errors.email = 'Email is required'
    } else if (!emailRegex.test(formData.email.trim())) {
      errors.email = 'Invalid email address'
    }
    if (!formData.username.trim()) {
      errors.username = 'Username is required'
    }
    if (!formData.clinicIds.trim()) {
      errors.clinicIds = 'Select at least one clinic'
    }
    if (!formData.workRole.trim()) {
      errors.workRole = 'Work role is required'
    }
    if (
      atProviderSeatLimit &&
      !keepsExistingSeat &&
      formData.workRole.trim() &&
      isProviderSeatRole(formData.workRole)
    ) {
      errors.workRole = SEAT_LIMIT_NOTE
    }
    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }, [formData, atProviderSeatLimit, keepsExistingSeat])

  const handleNext = useCallback((): void => {
    if (step === 1 && validateStep1()) {
      setStep(2)
    }
  }, [step, validateStep1])

  const handlePrevious = useCallback((): void => {
    if (step > 1) {
      setStep(step - 1)
    }
  }, [step])

  const handleClose = useCallback((): void => {
    setFormData(emptyForm())
    setStep(1)
    setValidationErrors({})
    onRequestClose()
  }, [onRequestClose])

  const handleSave = useCallback((): void => {
    if (!validateStep1()) {
      setStep(1)
      return
    }

    const payload: StaffSettingsFormData = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email.trim(),
      username: formData.username.trim(),
      clinicIds: formData.clinicIds.trim(),
      workRole: formData.workRole.trim(),
    }

    if (onSubmit) {
      onSubmit(payload)
      handleClose()
      return
    }

    const selectedClinicIds = splitIds(payload.clinicIds)
    const primaryClinicId = selectedClinicIds[0]

    if (!primaryClinicId) {
      showToast(
        createFailedToast(
          'Failed to create staff',
          'Please select at least one clinic under Assigned to Clinic.',
        ),
      )
      return
    }

    if (initialProviderData?.providerId) {
      const updated: ProviderData = {
        ...initialProviderData,
        clinicId: selectedClinicIds.includes(initialProviderData.clinicId)
          ? initialProviderData.clinicId
          : primaryClinicId,
        clinicIds: selectedClinicIds,
        workRole: payload.workRole,
        firstName: payload.firstName,
        lastName: payload.lastName,
        email: payload.email,
        username: payload.username,
      }
      const changes = getChangedProviderFields(initialProviderData, updated)
      if (Object.keys(changes).length === 0) {
        handleClose()
        return
      }
      dispatch(
        updateProvider({
          id: initialProviderData.providerId,
          provider: changes,
        }),
      )
        .unwrap()
        .then(() => {
          showToast(
            createSuccessToast(
              'Staff updated successfully',
              'Staff details have been updated.',
            ),
          )
          handleClose()
          onSaved?.()
        })
        .catch((error) => {
          showToast(
            createFailedToast(
              'Failed to update staff',
              typeof error === 'string'
                ? error
                : 'Failed to update staff. Please try again.',
            ),
          )
        })
      return
    }

    const providerData: ProviderData = {
      ...(createEmptyProviderData() as ProviderData),
      clinicId: primaryClinicId,
      clinicIds: selectedClinicIds,
      workRole: payload.workRole,
      firstName: payload.firstName,
      lastName: payload.lastName,
      email: payload.email,
      username: payload.username,
      // Required by backend validator but not collected in this Settings modal.
      gender: 'Female',
      dateOfBirth: '1990-01-01',
      address: 'N/A',
      city: 'N/A',
      state: 'N/A',
      zip: '00000',
      mobile: '+15550000000',
    }

    dispatch(addProvider(providerData))
      .unwrap()
      .then(({ provider }) => {
        showToast(
          createSuccessToast(
            'Staff created successfully',
            'Staff has been created and added to the system.',
          ),
        )
        handleClose()
        onSaved?.()
        void sendStaffEmail({
          email: payload.email,
          firstName: payload.firstName,
          lastName: payload.lastName,
          username: payload.username,
          providerId: provider.provider_id,
        })
      })
      .catch((error) => {
        showToast(
          createFailedToast(
            'Failed to create staff',
            error instanceof Error
              ? error.message
              : 'Failed to create staff. Please try again.',
          ),
        )
      })
  }, [
    dispatch,
    formData,
    handleClose,
    initialProviderData,
    onSaved,
    onSubmit,
    showToast,
    validateStep1,
  ])

  const assignedClinicName = useMemo(() => {
    const ids = splitIds(formData.clinicIds)
    if (ids.length === 0) return '—'
    return ids
      .map((id) => clinicOptions.find((c) => c.value === id)?.label ?? id)
      .join(', ')
  }, [clinicOptions, formData.clinicIds])

  const textField = (
    field: keyof StaffSettingsFormData,
    label: string,
    placeholder: string,
    extra: Partial<React.ComponentProps<typeof InputField>> = {},
  ): React.ReactElement => (
    <InputField
      fullWidth
      variant="text"
      label={label}
      placeholder={placeholder}
      value={formData[field]}
      onChange={(e) => handleInputChange(field, e.target.value)}
      error={!!validationErrors[field]}
      helperText={validationErrors[field] || undefined}
      {...extra}
    />
  )

  const renderStep1 = (): React.ReactElement => (
    <div className="add-staff-form">
      <div className="add-staff-row">
        {textField('firstName', 'First name', 'ex. John', {
          onBlur: (e: React.FocusEvent<HTMLInputElement>) =>
            handleNameBlur('firstName', e.target.value),
        })}
        {textField('lastName', 'Last name', 'ex. Doe', {
          onBlur: (e: React.FocusEvent<HTMLInputElement>) =>
            handleNameBlur('lastName', e.target.value),
        })}
      </div>
      <div className="add-staff-row">
        {textField('email', 'Email', 'ex. john.doe@email.com', {
          variant: 'email',
        })}
        {textField('username', 'Username', 'Type a username')}
      </div>
      <MultiselectCombobox
        label="Assigned to clinic"
        placeholder="Select clinics"
        options={clinicOptions}
        value={splitIds(formData.clinicIds)}
        onChange={(values) => handleInputChange('clinicIds', values.join(','))}
        error={!!validationErrors.clinicIds}
        helperText={validationErrors.clinicIds || undefined}
      />
      <Select
        label="Work role"
        placeholder="Select"
        options={workRoleOptions}
        value={formData.workRole}
        onChange={(value) => handleInputChange('workRole', String(value))}
        error={!!validationErrors.workRole}
        helperText={
          validationErrors.workRole ||
          (atProviderSeatLimit && !keepsExistingSeat
            ? SEAT_LIMIT_NOTE
            : undefined)
        }
      />
    </div>
  )

  const renderReview = (): React.ReactElement => (
    <div className="add-staff-review-step">
      <Accordion className="add-staff-review" defaultOpenIds={['personal']}>
        <AccordionItem id="personal" title="Personal">
          <DescriptionList
            columns={1}
            variant="plain"
            items={[
              {
                label: 'Name',
                value: `${formData.firstName} ${formData.lastName}`.trim(),
              },
              { label: 'Email', value: formData.email || '—' },
              { label: 'Username', value: formData.username || '—' },
              { label: 'Assigned to clinic', value: assignedClinicName },
              { label: 'Work role', value: formData.workRole || '—' },
            ]}
          />
        </AccordionItem>
      </Accordion>
      <div className="add-staff-review-actions">
        <Button
          variant="tertiary"
          size="md"
          type="button"
          onClick={() => setStep(1)}
        >
          Edit
        </Button>
      </div>
    </div>
  )

  const isReviewStep = step === STEPS.length

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      size="md"
      title={isEdit ? 'Edit staff' : 'Add staff'}
      closeOnBackdrop={false}
      className="add-staff-modal"
      bodyClassName="add-staff-modal__body"
      primaryLabel={isReviewStep ? 'Save' : 'Next'}
      onPrimaryClick={isReviewStep ? handleSave : handleNext}
      secondaryLabel={step > 1 ? 'Previous' : ''}
      onSecondaryClick={handlePrevious}
    >
      <Stepper
        className="add-staff-modal__stepper"
        steps={STEPS}
        currentStep={step - 1}
      />
      <form
        onSubmit={(e): void => {
          e.preventDefault()
        }}
      >
        {isReviewStep ? renderReview() : renderStep1()}
      </form>
    </Modal>
  )
}

export default AddStaff
